import { Select as KobalteSelect } from "@kobalte/core/select";
import { Check, ChevronsUpDown, Plus } from "lucide-solid";
import { type JSX, Show } from "solid-js";
import { cn } from "@bso/shared";

/**
 * Premium Select built on @kobalte/core/select.
 *
 * Mirrors the patterns in `popover.tsx` / `dialog.tsx`: theme-safe tokens
 * (bg-card/popover, border, ring, accent), an input-like trigger, an animated
 * portal panel, a check on the selected item, and full keyboard / typeahead /
 * ARIA support out of the box from Kobalte.
 *
 * Generic over an arbitrary value type `V`. Options are described declaratively
 * so any native `<select>` (string OR number values) can be replaced 1:1 while
 * preserving its value / onChange / option list.
 */

interface SelectOption<V> {
  value: V;
  label: JSX.Element;
  /** Plain-text value used for typeahead. Falls back to String(value). */
  textValue?: string;
  disabled?: boolean;
}

/**
 * CTA rendered inside the dropdown when the Select has no options (AWS-style:
 * an empty combobox offers "Create X" / "Go to X" instead of a dead control).
 *
 * Pass `onSelect` to run an action (open a creation dialog) or `href` to render
 * a link (navigate to the screen that provides the options). Provide exactly one.
 * `can` (default `true`) gates the CTA on permission — when `false` the empty
 * dropdown falls back to a plain "no options" message and no creation affordance
 * is shown. Omit `emptyAction` entirely when the source is not creatable.
 */
export interface SelectEmptyAction {
  label: string;
  onSelect?: () => void;
  href?: string;
  /** Permission gate. When false, the CTA is hidden. Defaults to true. */
  can?: boolean;
  /** Leading icon for the CTA. Defaults to a plus glyph. */
  icon?: JSX.Element;
  /** Optional helper line shown above the CTA (e.g. "No lists yet"). */
  description?: string;
}

interface SelectProps<V> {
  value: V;
  onChange: (value: V) => void;
  options: SelectOption<V>[];
  placeholder?: JSX.Element;
  disabled?: boolean;
  /** Accessible label for the trigger. */
  "aria-label"?: string;
  id?: string;
  /** Extra classes for the trigger. */
  class?: string;
  /** Size preset for the trigger. */
  size?: "sm" | "md";
  /**
   * Shown inside the dropdown when `options` is empty. Lets the user jump to the
   * place that creates options instead of facing a dead Select. See
   * {@link SelectEmptyAction}.
   */
  emptyAction?: SelectEmptyAction;
  /** Message shown in the empty dropdown when there is no (permitted) CTA. */
  emptyLabel?: string;
}

export function Select<V>(props: SelectProps<V>) {
  const selectedOption = () => props.options.find((o) => Object.is(o.value, props.value));

  return (
    <KobalteSelect<SelectOption<V>>
      options={props.options}
      value={selectedOption() ?? null}
      onChange={(option) => {
        if (option) {
          props.onChange(option.value);
        }
      }}
      optionValue={(o) => o.textValue ?? String(o.value)}
      optionTextValue={(o) => o.textValue ?? String(o.value)}
      optionDisabled={(o) => o.disabled ?? false}
      disabled={props.disabled}
      disallowEmptySelection
      placeholder={props.placeholder}
      gutter={6}
      sameWidth
      itemComponent={(itemProps) => (
        <KobalteSelect.Item
          item={itemProps.item}
          class={cn(
            "relative flex cursor-pointer select-none items-center gap-2 rounded-lg py-2 pl-3 pr-8 text-sm outline-none transition-colors",
            "text-foreground data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
            "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          )}
        >
          <KobalteSelect.ItemLabel class="truncate">
            {itemProps.item.rawValue.label}
          </KobalteSelect.ItemLabel>
          <KobalteSelect.ItemIndicator class="absolute right-2 flex h-4 w-4 items-center justify-center text-accent-foreground">
            <Check class="h-4 w-4" />
          </KobalteSelect.ItemIndicator>
        </KobalteSelect.Item>
      )}
    >
      <KobalteSelect.Trigger
        id={props.id}
        aria-label={props["aria-label"]}
        class={cn(
          "bso-select-trigger inline-flex items-center justify-between gap-2 rounded-[10px] border border-border/50 bg-muted/40 text-foreground outline-none transition-all duration-150",
          "hover:bg-muted/70 hover:border-border/80",
          "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0",
          "disabled:cursor-not-allowed disabled:opacity-50",
          props.size === "md" ? "h-10 px-3.5 text-sm" : "h-9 px-3 text-xs font-semibold",
          props.class,
        )}
      >
        <KobalteSelect.Value<SelectOption<V>> class="truncate">
          {(state) => state.selectedOption()?.label}
        </KobalteSelect.Value>
        <KobalteSelect.Icon class="shrink-0 opacity-60">
          <ChevronsUpDown class="h-3.5 w-3.5" />
        </KobalteSelect.Icon>
      </KobalteSelect.Trigger>
      <KobalteSelect.Portal>
        <KobalteSelect.Content
          class={cn(
            "ui-select-content z-[220] overflow-hidden rounded-xl border border-border bg-card text-foreground shadow-2xl outline-none",
          )}
        >
          <Show
            when={props.options.length > 0}
            fallback={
              <SelectEmptyContent
                action={props.emptyAction}
                label={props.emptyLabel}
                placeholder={props.placeholder}
              />
            }
          >
            <KobalteSelect.Listbox class="max-h-72 overflow-y-auto p-1" />
          </Show>
        </KobalteSelect.Content>
      </KobalteSelect.Portal>
    </KobalteSelect>
  );
}

/**
 * Content shown inside an empty Select dropdown. Renders the `emptyAction` CTA
 * (creation button or navigation link) when present and permitted, otherwise a
 * muted message (the call-site `label`, else the `placeholder`, else an em-dash).
 *
 * The CTA is keyboard-reachable (it is a native button/anchor, auto-focusable
 * inside the portalled panel) and inherits the panel's opacity-only entrance, so
 * it respects `prefers-reduced-motion` via the global guard like the rest of the UI.
 */
export function SelectEmptyContent(props: {
  action?: SelectEmptyAction;
  label?: string;
  placeholder?: JSX.Element;
}) {
  const ctaClass = cn(
    "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium outline-none",
    "text-foreground transition-colors cursor-pointer",
    "hover:bg-accent hover:text-accent-foreground",
    "focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
  );

  const ctaIcon = () =>
    props.action?.icon ?? <Plus class="h-4 w-4 shrink-0 text-accent-foreground" />;

  return (
    <Show
      when={props.action && props.action.can !== false ? props.action : undefined}
      fallback={
        <div class="px-3 py-2 text-xs text-muted-foreground">
          {/* Copy lives at the call site so this primitive stays i18n-clean; the
              neutral em-dash is the last-resort placeholder when nothing is given. */}
          {props.label ?? props.placeholder ?? "—"}
        </div>
      }
    >
      {(action) => (
        <div class="space-y-1 p-1">
          <Show when={action().description}>
            <p class="px-3 pt-1 text-xs text-muted-foreground">{action().description}</p>
          </Show>
          <Show
            when={action().href}
            fallback={
              <button
                type="button"
                class={ctaClass}
                onClick={() => {
                  action().onSelect?.();
                }}
              >
                {ctaIcon()}
                <span class="truncate">{action().label}</span>
              </button>
            }
          >
            <a href={action().href} class={ctaClass}>
              {ctaIcon()}
              <span class="truncate">{action().label}</span>
            </a>
          </Show>
        </div>
      )}
    </Show>
  );
}
